import { type ReactNode } from 'react';
import { Badge, Button, CheckIcon, Modal, formatExact } from '@spacegame/ui';
import type { ResourceAmounts } from '@spacegame/engine';

const RESOURCE_LABEL: Record<string, string> = {
  titan: 'Titan-Legierung',
  silicate: 'Kristall-Silikat',
  isotope: 'Isotop-3',
  chronos: 'Chronos-Kristalle',
};

export interface ShopCheckoutModalProps {
  open: boolean;
  status: 'confirm' | 'success';
  productLabel: string;
  priceLabel: string;
  art: ReactNode;
  /** What the purchase grants — shown as a checklist before and after confirming. */
  grant: ResourceAmounts;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * The mock checkout: click "Kauf bestätigen", the purchase is granted
 * instantly. No payment provider exists yet (docs/TODO.md) — this is the
 * owner-requested placeholder ("Bezahlvorgang überspringen und so tun als
 * hätte man gezahlt"), so the copy says so plainly rather than pretending a
 * real transaction happened.
 */
export function ShopCheckoutModal({
  open,
  status,
  productLabel,
  priceLabel,
  art,
  grant,
  onConfirm,
  onClose,
}: ShopCheckoutModalProps) {
  const entries = Object.entries(grant).filter(([, amount]) => (amount ?? 0) > 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      eyebrow={status === 'confirm' ? 'Kauf bestätigen' : 'Kauf abgeschlossen'}
      title={productLabel}
      dismissible={status === 'confirm'}
      footer={
        status === 'confirm' ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              Abbrechen
            </Button>
            <Button variant="premium" onClick={onConfirm}>
              Kauf bestätigen — {priceLabel}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={onClose} fullWidth>
            Weiter shoppen
          </Button>
        )
      }
    >
      <div className="relative flex flex-col items-center gap-3 py-2">
        <div className={status === 'success' ? 'sg-reel-settle flex h-20 items-center justify-center' : 'flex h-20 items-center justify-center'}>
          {art}
        </div>

        {status === 'success' ? (
          <Badge accent="success" variant="soft" icon={<CheckIcon size={14} />}>
            Erfolgreich gutgeschrieben
          </Badge>
        ) : null}

        <dl className="w-full space-y-1.5 rounded-control border border-edge-subtle bg-surface-2 px-3 py-2.5">
          {entries.map(([resource, amount]) => (
            <div key={resource} className="flex items-baseline justify-between gap-3">
              <dt className="text-xs text-content-secondary">{RESOURCE_LABEL[resource] ?? resource}</dt>
              <dd data-sg-numeric className="text-xs font-semibold text-content">
                +{formatExact(amount ?? 0)}
              </dd>
            </div>
          ))}
        </dl>

        {status === 'confirm' ? (
          <p className="text-center text-xs leading-relaxed text-content-muted">
            Demo-Modus: Es ist noch kein Zahlungsanbieter angebunden. Der Kauf wird sofort
            gutgeschrieben, ohne dass echtes Geld abgebucht wird.
          </p>
        ) : (
          <p className="text-center text-xs leading-relaxed text-content-muted">
            Vielen Dank für deine Unterstützung, Kommandant.
          </p>
        )}
      </div>
    </Modal>
  );
}
